<template>
  <div class="master-data-management">
    <el-tabs v-model="activeTab" class="master-tabs">
      <el-tab-pane label="合作连锁店" name="chains">
        <div class="toolbar">
          <el-input v-model="chainKeyword" placeholder="搜索连锁店名称、编码" clearable class="toolbar-input" />
          <el-button type="primary" :loading="loadingChains" @click="fetchChains">刷新</el-button>
          <el-button v-if="canManageMasterData" type="success" @click="openChainDialog()">新增连锁店</el-button>
        </div>

        <el-table :data="paginatedChains" v-loading="loadingChains" border stripe size="small">
          <el-table-column prop="name" label="连锁店名称" min-width="180" />
          <el-table-column prop="code" label="连锁店编码" min-width="140" />
          <el-table-column label="启用" width="90">
            <template #default="{ row }">
              <el-tag :type="row.enabled ? 'success' : 'info'">{{ row.enabled ? '是' : '否' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="description" label="说明" min-width="220" />
          <el-table-column v-if="canManageMasterData" label="操作" width="160" fixed="right">
            <template #default="{ row }">
              <el-button type="primary" text @click="openChainDialog(row)">编辑</el-button>
              <el-button type="danger" text @click="removeChain(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination-wrapper">
          <el-pagination
            v-model:current-page="currentChainPage"
            :page-size="pageSize"
            layout="total, prev, pager, next"
            :total="filteredChains.length"
          />
        </div>
      </el-tab-pane>

      <el-tab-pane label="产品目录" name="products">
        <div class="toolbar">
          <el-input v-model="productKeyword" placeholder="搜索产品名称、型号、说明" clearable class="toolbar-input" />
          <el-button type="primary" :loading="loadingProducts" @click="fetchProducts">刷新</el-button>
          <el-button v-if="userStore.isAdmin" :loading="syncingLlms" @click="triggerLlmsSync">从官网同步产品</el-button>
          <el-button v-if="userStore.isAdmin" type="success" @click="openProductDialog()">新增产品</el-button>
        </div>

        <el-table :data="paginatedProducts" v-loading="loadingProducts" border stripe size="small">
          <el-table-column prop="productName" label="产品名称" min-width="150" />
          <el-table-column prop="productModel" label="产品型号" min-width="110" />
          <el-table-column prop="deviceVersion" label="版本" min-width="90" />
          <el-table-column label="品牌系列" min-width="120">
            <template #default="{ row }">
              {{ row.chainBrandName || '—' }}
            </template>
          </el-table-column>
          <el-table-column label="图片" min-width="110">
            <template #default="{ row }">
              <el-image
                v-if="row.imageUrl"
                :src="row.imageUrl"
                fit="contain"
                :preview-src-list="[row.imageUrl]"
                preview-teleported
                style="width: 64px; height: 42px; border-radius: 4px; background: #f5f7fa"
              />
              <span v-else>—</span>
            </template>
          </el-table-column>
          <el-table-column label="启用" width="80">
            <template #default="{ row }">
              <el-tag :type="row.enabled ? 'success' : 'info'">{{ row.enabled ? '是' : '否' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="description" label="说明" min-width="200" />
          <el-table-column v-if="userStore.isAdmin" label="操作" width="160" fixed="right">
            <template #default="{ row }">
              <el-button type="primary" text @click="openProductDialog(row)">编辑</el-button>
              <el-button type="danger" text @click="removeProduct(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination-wrapper">
          <el-pagination
            v-model:current-page="currentProductPage"
            :page-size="pageSize"
            layout="total, prev, pager, next"
            :total="filteredProducts.length"
          />
        </div>
      </el-tab-pane>

      <el-tab-pane v-if="FEATURE_DEVICE_ARCHIVES" label="设备资料" name="devices">
        <div class="toolbar">
          <el-input v-model="deviceKeyword" placeholder="搜索设备名称、保修备注、说明" clearable class="toolbar-input" />
          <el-button type="primary" :loading="loadingDevices" @click="fetchDevices">刷新</el-button>
          <el-button v-if="canManageMasterData" @click="downloadDeviceTemplate">下载导入模板</el-button>
          <el-upload
            v-if="canManageMasterData"
            :show-file-list="false"
            accept=".xlsx,.xls"
            :before-upload="beforeImportDevice"
          >
            <el-button type="warning" :loading="importingDevices">导入 Excel</el-button>
          </el-upload>
          <el-button v-if="canManageMasterData" type="success" @click="openDeviceDialog()">新增设备</el-button>
        </div>

        <el-table :data="paginatedDevices" v-loading="loadingDevices" border stripe size="small">
          <el-table-column prop="deviceName" label="设备名称" min-width="150" />
          <el-table-column label="所属连锁店" min-width="160">
            <template #default="{ row }">
              {{ getChainName(row.cooperativeChainId) }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="120">
            <template #default="{ row }">
              <el-tag :type="row.status === 'PRODUCTION' ? 'success' : 'info'">{{ getDeviceStatusText(row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="warrantyRemark" label="保修备注" min-width="180" />
          <el-table-column prop="description" label="说明" min-width="180" />
          <el-table-column v-if="canManageMasterData" label="操作" width="160" fixed="right">
            <template #default="{ row }">
              <el-button type="primary" text @click="openDeviceDialog(row)">编辑</el-button>
              <el-button type="danger" text @click="removeDevice(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination-wrapper">
          <el-pagination
            v-model:current-page="currentDevicePage"
            :page-size="pageSize"
            layout="total, prev, pager, next"
            :total="filteredDevices.length"
          />
        </div>
      </el-tab-pane>

      <el-tab-pane label="配件资料" name="parts">
        <div class="toolbar">
          <el-input v-model="partKeyword" placeholder="搜索标准名称、别名、品牌、型号" clearable class="toolbar-input" />
          <el-button type="primary" :loading="loadingParts" @click="fetchParts">刷新</el-button>
          <el-button v-if="canManageMasterData" @click="downloadPartTemplate">下载导入模板</el-button>
          <el-upload
            v-if="canManageMasterData"
            :show-file-list="false"
            accept=".xlsx,.xls"
            :before-upload="beforeImportPart"
          >
            <el-button type="warning" :loading="importingParts">导入 Excel</el-button>
          </el-upload>
          <el-button v-if="canManageMasterData" type="success" @click="openPartDialog()">新增配件</el-button>
        </div>

        <el-table :data="paginatedParts" v-loading="loadingParts" border stripe size="small">
          <el-table-column prop="standardName" label="标准名称" min-width="160" />
          <el-table-column prop="aliasName" label="别名" min-width="120" />
          <el-table-column prop="brandName" label="品牌" min-width="120" />
          <el-table-column prop="partModel" label="配件型号" min-width="120" />
          <el-table-column prop="versionNo" label="版本号" min-width="120" />
          <el-table-column label="默认单价" min-width="120">
            <template #default="{ row }">
              {{ formatMoney(row.defaultUnitPrice) }}
            </template>
          </el-table-column>
          <el-table-column label="优化件" width="90">
            <template #default="{ row }">
              <el-tag :type="row.isOptimized ? 'success' : 'info'">{{ row.isOptimized ? '是' : '否' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="启用" width="90">
            <template #default="{ row }">
              <el-tag :type="row.enabled ? 'success' : 'info'">{{ row.enabled ? '是' : '否' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="description" label="说明" min-width="180" />
          <el-table-column v-if="canManageMasterData" label="操作" width="160" fixed="right">
            <template #default="{ row }">
              <el-button type="primary" text @click="openPartDialog(row)">编辑</el-button>
              <el-button type="danger" text @click="removePart(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination-wrapper">
          <el-pagination
            v-model:current-page="currentPartPage"
            :page-size="pageSize"
            layout="total, prev, pager, next"
            :total="filteredParts.length"
          />
        </div>
      </el-tab-pane>
    </el-tabs>

    <el-dialog v-model="deviceDialogVisible" :title="deviceForm.id ? '编辑设备资料' : '新增设备资料'" width="620px">
      <el-form :model="deviceForm" label-width="96px">
        <el-form-item label="所属连锁店" required>
          <el-select v-model="deviceForm.cooperativeChainId" clearable filterable style="width: 100%" placeholder="请选择所属合作连锁店">
            <el-option
              v-for="item in cooperativeChains"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="设备名称" required>
          <el-input v-model="deviceForm.deviceName" maxlength="100" />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="deviceForm.status" style="width: 100%">
            <el-option label="生产中" value="PRODUCTION" />
            <el-option label="已停产" value="DISCONTINUED" />
          </el-select>
        </el-form-item>
        <el-form-item label="保修备注">
          <el-input v-model="deviceForm.warrantyRemark" type="textarea" :rows="3" maxlength="500" show-word-limit />
        </el-form-item>
        <el-form-item label="说明">
          <el-input v-model="deviceForm.description" type="textarea" :rows="3" maxlength="255" show-word-limit />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="deviceDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="savingDevice" @click="submitDevice">保存</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="partDialogVisible" :title="partForm.id ? '编辑配件资料' : '新增配件资料'" width="560px">
      <el-form :model="partForm" label-width="96px">
        <el-form-item label="标准名称" required>
          <el-input v-model="partForm.standardName" maxlength="100" />
        </el-form-item>
        <el-form-item label="别名">
          <el-input v-model="partForm.aliasName" maxlength="100" />
        </el-form-item>
        <el-form-item label="品牌">
          <el-input v-model="partForm.brandName" maxlength="100" />
        </el-form-item>
        <el-form-item label="配件型号">
          <el-input v-model="partForm.partModel" maxlength="100" />
        </el-form-item>
        <el-form-item label="版本号">
          <el-input v-model="partForm.versionNo" maxlength="100" />
        </el-form-item>
        <el-form-item label="默认单价">
          <el-input-number v-model="partForm.defaultUnitPrice" :min="0" :precision="2" controls-position="right" style="width: 100%" />
        </el-form-item>
        <el-form-item label="优化件">
          <el-switch v-model="partForm.isOptimized" />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="partForm.enabled" />
        </el-form-item>
        <el-form-item label="说明">
          <el-input v-model="partForm.description" type="textarea" :rows="3" maxlength="255" show-word-limit />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="partDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="savingPart" @click="submitPart">保存</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="chainDialogVisible" :title="chainForm.id ? '编辑合作连锁店' : '新增合作连锁店'" width="520px">
      <el-form :model="chainForm" label-width="96px">
        <el-form-item label="连锁店名称" required>
          <el-input v-model="chainForm.name" maxlength="100" />
        </el-form-item>
        <el-form-item label="连锁店编码" required>
          <el-input v-model="chainForm.code" maxlength="50" />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="chainForm.enabled" />
        </el-form-item>
        <el-form-item label="说明">
          <el-input v-model="chainForm.description" type="textarea" :rows="3" maxlength="255" show-word-limit />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="chainDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="savingChain" @click="submitChain">保存</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="productDialogVisible" :title="productForm.id ? '编辑产品' : '新增产品'" width="640px">
      <el-form :model="productForm" label-width="96px">
        <el-form-item label="品牌系列" required>
          <el-select
            v-model="productForm.chainBrandKey"
            filterable
            allow-create
            default-first-option
            clearable
            style="width: 100%"
            placeholder="选择已有品牌，或直接输入新品牌名称"
          >
            <el-option v-for="item in brands" :key="item.id" :label="item.name" :value="String(item.id)" />
          </el-select>
          <div class="form-tip">输入新名称保存时会自动创建品牌；此目录同时驱动官网首页产品区与 llms.txt</div>
        </el-form-item>
        <el-form-item label="产品名称" required>
          <el-input v-model="productForm.productName" maxlength="100" placeholder="如：节能自动煮面机" />
        </el-form-item>
        <el-form-item label="产品型号">
          <el-input v-model="productForm.productModel" maxlength="100" placeholder="如：NMAG-ZMJ15" />
        </el-form-item>
        <el-form-item label="设备版本">
          <el-input v-model="productForm.deviceVersion" maxlength="50" placeholder="如：V3" />
        </el-form-item>
        <el-form-item label="产品图片">
          <div class="image-picker">
            <el-select v-model="imageDir" placeholder="文件夹" style="width: 160px" @change="onImageDirChange">
              <el-option v-for="dir in imageDirOptions" :key="dir" :label="dir" :value="dir" />
            </el-select>
            <el-select
              v-model="pickedImage"
              placeholder="选择图片（可选）"
              filterable
              clearable
              style="flex: 1"
              :loading="loadingImageFiles"
              @change="onPickedImageChange"
            >
              <el-option v-for="f in imageFiles" :key="f" :label="f" :value="f" />
            </el-select>
          </div>
          <el-input
            v-model="productForm.imageUrl"
            placeholder="图片路径，如 /png/产品信息介绍图片/xxx.png（可手动填写）"
            style="width: 100%; margin-top: 6px"
          />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="productForm.enabled" />
        </el-form-item>
        <el-form-item label="说明">
          <el-input v-model="productForm.description" type="textarea" :rows="3" maxlength="255" show-word-limit placeholder="功率/电压/尺寸/应用场景等，将进入 llms.txt 供 AI 检索" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="productDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="savingProduct" @click="submitProduct">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { MasterDataAPI } from '@/request/masterData'
import { useUserStore } from '@/stores/user'
import { lowerText, trimText } from '@/utils/text'
import { FEATURE_DEVICE_ARCHIVES } from '@/config/featureFlags'

const createEmptyDeviceForm = () => ({
  id: null,
  cooperativeChainId: null,
  deviceName: '',
  warrantyRemark: '',
  status: 'PRODUCTION',
  description: ''
})

const createEmptyChainForm = () => ({
  id: null,
  name: '',
  code: '',
  enabled: true,
  description: ''
})

const createEmptyPartForm = () => ({
  id: null,
  standardName: '',
  aliasName: '',
  brandName: '',
  partModel: '',
  versionNo: '',
  defaultUnitPrice: 0,
  isOptimized: false,
  enabled: true,
  description: ''
})

const createEmptyProductForm = () => ({
  id: null,
  chainBrandKey: '',
  productName: '',
  productModel: '',
  deviceVersion: '',
  imageUrl: '',
  enabled: true,
  description: ''
})

export default {
  name: 'MasterDataManagement',
  props: {
    initialTab: {
      type: String,
      default: 'products'
    }
  },
  setup(props) {
    const userStore = useUserStore()
    const canManageMasterData = computed(() => userStore.isAdmin || userStore.isAfterSales)
    const activeTab = ref(['products', 'parts', 'chains'].includes(props.initialTab) ? props.initialTab : 'products')
    const chainKeyword = ref('')
    const deviceKeyword = ref('')
    const partKeyword = ref('')
    const productKeyword = ref('')
    const currentChainPage = ref(1)
    const currentDevicePage = ref(1)
    const currentPartPage = ref(1)
    const currentProductPage = ref(1)
    const pageSize = 10
    const loadingChains = ref(false)
    const loadingDevices = ref(false)
    const loadingParts = ref(false)
    const loadingProducts = ref(false)
    const savingChain = ref(false)
    const savingDevice = ref(false)
    const savingPart = ref(false)
    const savingProduct = ref(false)
    const syncingLlms = ref(false)
    const importingDevices = ref(false)
    const importingParts = ref(false)
    const cooperativeChains = ref([])
    const devices = ref([])
    const parts = ref([])
    const products = ref([])
    const brands = ref([])
    const chainDialogVisible = ref(false)
    const deviceDialogVisible = ref(false)
    const partDialogVisible = ref(false)
    const productDialogVisible = ref(false)
    const chainForm = reactive(createEmptyChainForm())
    const deviceForm = reactive(createEmptyDeviceForm())
    const partForm = reactive(createEmptyPartForm())
    const productForm = reactive(createEmptyProductForm())
    const imageDirOptions = ['纯设备图片', '产品信息介绍图片', '展示的产品']
    const imageDir = ref('')
    const imageFiles = ref([])
    const loadingImageFiles = ref(false)
    const pickedImage = ref('')

    const filteredChains = computed(() => {
      const keyword = lowerText(chainKeyword.value)
      if (!keyword) return cooperativeChains.value
      return cooperativeChains.value.filter((item) => `${item.name || ''} ${item.code || ''}`.toLowerCase().includes(keyword))
    })
    const paginatedChains = computed(() => {
      const start = (currentChainPage.value - 1) * pageSize
      return filteredChains.value.slice(start, start + pageSize)
    })

    function getDeviceStatusText(value) {
      return ({
        PRODUCTION: '生产中',
        DISCONTINUED: '已停产'
      }[value] || value || '—')
    }

    function getChainName(id) {
      if (!id) return '—'
      return cooperativeChains.value.find((item) => item.id === id)?.name || `#${id}`
    }

    const filteredDevices = computed(() => {
      const keyword = lowerText(deviceKeyword.value)
      if (!keyword) return devices.value
      return devices.value.filter((item) => {
        const chainName = item.cooperativeChainId ? getChainName(item.cooperativeChainId) : ''
        const text = [
          item.deviceName,
          chainName,
          getDeviceStatusText(item.status),
          item.warrantyRemark,
          item.description
        ].filter(Boolean).join(' ').toLowerCase()
        return text.includes(keyword)
      })
    })
    const paginatedDevices = computed(() => {
      const start = (currentDevicePage.value - 1) * pageSize
      return filteredDevices.value.slice(start, start + pageSize)
    })

    const filteredParts = computed(() => {
      const keyword = lowerText(partKeyword.value)
      if (!keyword) return parts.value
      return parts.value.filter((item) => `${item.standardName || ''} ${item.aliasName || ''} ${item.brandName || ''} ${item.partModel || ''}`.toLowerCase().includes(keyword))
    })
    const paginatedParts = computed(() => {
      const start = (currentPartPage.value - 1) * pageSize
      return filteredParts.value.slice(start, start + pageSize)
    })

    const filteredProducts = computed(() => {
      const keyword = lowerText(productKeyword.value)
      if (!keyword) return products.value
      return products.value.filter((item) => `${item.productName || ''} ${item.productModel || ''} ${item.deviceVersion || ''} ${item.chainBrandName || ''} ${item.description || ''}`.toLowerCase().includes(keyword))
    })
    const paginatedProducts = computed(() => {
      const start = (currentProductPage.value - 1) * pageSize
      return filteredProducts.value.slice(start, start + pageSize)
    })

    const fetchChains = async () => {
      try {
        loadingChains.value = true
        const list = await MasterDataAPI.getCooperativeChains()
        cooperativeChains.value = Array.isArray(list) ? list : []
      } catch (err) {
        ElMessage.error(err?.message || '获取合作连锁店失败')
      } finally {
        loadingChains.value = false
      }
    }

    const fetchDevices = async () => {
      try {
        loadingDevices.value = true
        const list = await MasterDataAPI.getDeviceArchives()
        devices.value = Array.isArray(list) ? list : []
      } catch (err) {
        ElMessage.error(err?.message || '获取设备资料失败')
      } finally {
        loadingDevices.value = false
      }
    }

    const fetchParts = async () => {
      try {
        loadingParts.value = true
        const list = await MasterDataAPI.getParts()
        parts.value = Array.isArray(list) ? list : []
      } catch (err) {
        ElMessage.error(err?.message || '获取配件资料失败')
      } finally {
        loadingParts.value = false
      }
    }

    const fetchProducts = async () => {
      try {
        loadingProducts.value = true
        const list = await MasterDataAPI.getBrandProducts()
        products.value = Array.isArray(list) ? list : []
      } catch (err) {
        ElMessage.error(err?.message || '获取产品目录失败')
      } finally {
        loadingProducts.value = false
      }
    }

    const fetchBrands = async () => {
      try {
        const list = await MasterDataAPI.getBrands()
        brands.value = Array.isArray(list) ? list : []
      } catch (err) {
        ElMessage.error(err?.message || '获取品牌列表失败')
      }
    }

    const onImageDirChange = async (dir) => {
      pickedImage.value = ''
      imageFiles.value = []
      if (!dir) return
      try {
        loadingImageFiles.value = true
        const res = await fetch(`/png-list/${encodeURIComponent(dir)}/`)
        if (!res.ok) return
        const data = await res.json()
        imageFiles.value = (Array.isArray(data) ? data.filter((item) => item?.type === 'file').map((item) => item.name) : (data.files || []))
          .filter((f) => /\.(png|jpe?g|gif|webp)$/i.test(f))
      } catch (err) {
        ElMessage.error(err?.message || '读取图片列表失败')
      } finally {
        loadingImageFiles.value = false
      }
    }

    const onPickedImageChange = (file) => {
      if (file && imageDir.value) {
        productForm.imageUrl = `/png/${imageDir.value}/${file}`
      }
    }

    const openProductDialog = (row) => {
      Object.assign(productForm, createEmptyProductForm(), row ? {
        id: row.id,
        chainBrandKey: row.chainBrandId ? String(row.chainBrandId) : (row.chainBrandName || ''),
        productName: row.productName || '',
        productModel: row.productModel || '',
        deviceVersion: row.deviceVersion || '',
        imageUrl: row.imageUrl || '',
        enabled: !!row.enabled,
        description: row.description || ''
      } : {})
      const match = /\/png\/([^/]+)\//.exec(productForm.imageUrl || '')
      if (match && imageDirOptions.includes(decodeURIComponent(match[1]))) {
        imageDir.value = decodeURIComponent(match[1])
        onImageDirChange(imageDir.value)
      } else {
        imageDir.value = ''
        imageFiles.value = []
        pickedImage.value = ''
      }
      productDialogVisible.value = true
    }

    const resolveBrandId = async () => {
      const key = String(productForm.chainBrandKey || '').trim()
      if (!key) return null
      const byId = brands.value.find((item) => String(item.id) === key)
      if (byId) return byId.id
      const byName = brands.value.find((item) => item.name === key)
      if (byName) return byName.id
      const created = await MasterDataAPI.createBrand({
        name: key,
        code: `BR${Date.now()}`,
        enabled: true,
        description: '产品目录维护时自动创建'
      })
      await fetchBrands()
      return created?.id || null
    }

    const submitProduct = async () => {
      if (!trimText(productForm.productName)) {
        ElMessage.warning('请填写产品名称')
        return
      }
      if (!String(productForm.chainBrandKey || '').trim()) {
        ElMessage.warning('请选择或输入品牌系列')
        return
      }
      try {
        savingProduct.value = true
        const chainBrandId = await resolveBrandId()
        if (!chainBrandId) {
          ElMessage.error('品牌系列解析失败，请重试')
          return
        }
        const payload = {
          chainBrandId,
          productName: trimText(productForm.productName),
          productModel: trimText(productForm.productModel) || null,
          deviceVersion: trimText(productForm.deviceVersion) || null,
          imageUrl: trimText(productForm.imageUrl) || null,
          enabled: !!productForm.enabled,
          description: trimText(productForm.description) || null
        }
        if (productForm.id) {
          await MasterDataAPI.updateBrandProduct(productForm.id, payload)
          ElMessage.success('产品已更新，官网首页与 llms.txt 即时生效')
        } else {
          await MasterDataAPI.createBrandProduct(payload)
          ElMessage.success('产品已创建，官网首页与 llms.txt 即时生效')
        }
        productDialogVisible.value = false
        await fetchProducts()
      } catch (err) {
        ElMessage.error(err?.message || '保存产品失败')
      } finally {
        savingProduct.value = false
      }
    }

    const removeProduct = async (row) => {
      try {
        await ElMessageBox.confirm(`确定删除产品“${row.productName}”吗？删除后官网首页与 llms.txt 将不再展示该产品。`, '提示', { type: 'warning' })
        await MasterDataAPI.deleteBrandProduct(row.id)
        ElMessage.success('产品已删除')
        await fetchProducts()
      } catch (err) {
        if (err !== 'cancel' && err !== 'close') {
          ElMessage.error(err?.message || '删除产品失败')
        }
      }
    }

    const triggerLlmsSync = async () => {
      try {
        syncingLlms.value = true
        const verdict = await MasterDataAPI.runLlmsSync()
        ElMessageBox.alert(String(verdict || '同步已执行'), '官网产品同步结果', { type: 'success' })
      } catch (err) {
        ElMessage.error(err?.message || '触发同步失败')
      } finally {
        syncingLlms.value = false
      }
    }

    const openChainDialog = (row) => {
      Object.assign(chainForm, createEmptyChainForm(), row || {})
      chainDialogVisible.value = true
    }

    const openDeviceDialog = (row) => {
      Object.assign(deviceForm, createEmptyDeviceForm(), row || {})
      deviceDialogVisible.value = true
    }

    const openPartDialog = (row) => {
      Object.assign(partForm, createEmptyPartForm(), row || {})
      partDialogVisible.value = true
    }

    const submitChain = async () => {
      if (!trimText(chainForm.name) || !trimText(chainForm.code)) {
        ElMessage.warning('请填写合作连锁店名称和编码')
        return
      }
      try {
        savingChain.value = true
        const payload = {
          name: trimText(chainForm.name),
          code: trimText(chainForm.code),
          enabled: !!chainForm.enabled,
          description: trimText(chainForm.description) || null
        }
        if (chainForm.id) {
          await MasterDataAPI.updateCooperativeChain(chainForm.id, payload)
          ElMessage.success('合作连锁店已更新')
        } else {
          await MasterDataAPI.createCooperativeChain(payload)
          ElMessage.success('合作连锁店已创建')
        }
        chainDialogVisible.value = false
        await fetchChains()
      } catch (err) {
        ElMessage.error(err?.message || '保存合作连锁店失败')
      } finally {
        savingChain.value = false
      }
    }

    const submitDevice = async () => {
      if (!trimText(deviceForm.deviceName)) {
        ElMessage.warning('请填写设备名称')
        return
      }
      if (!deviceForm.cooperativeChainId) {
        ElMessage.warning('请选择所属合作连锁店')
        return
      }
      try {
        savingDevice.value = true
        const payload = {
          cooperativeChainId: deviceForm.cooperativeChainId,
          deviceName: trimText(deviceForm.deviceName),
          warrantyRemark: trimText(deviceForm.warrantyRemark) || null,
          status: deviceForm.status || 'PRODUCTION',
          description: trimText(deviceForm.description) || null
        }
        if (deviceForm.id) {
          await MasterDataAPI.updateDeviceArchive(deviceForm.id, payload)
          ElMessage.success('设备资料已更新')
        } else {
          await MasterDataAPI.createDeviceArchive(payload)
          ElMessage.success('设备资料已创建')
        }
        deviceDialogVisible.value = false
        await fetchDevices()
      } catch (err) {
        ElMessage.error(err?.message || '保存设备资料失败')
      } finally {
        savingDevice.value = false
      }
    }

    const submitPart = async () => {
      if (!trimText(partForm.standardName)) {
        ElMessage.warning('请填写配件标准名称')
        return
      }
      try {
        savingPart.value = true
        const payload = {
          standardName: trimText(partForm.standardName),
          aliasName: trimText(partForm.aliasName) || null,
          brandName: trimText(partForm.brandName) || null,
          partModel: trimText(partForm.partModel) || null,
          versionNo: trimText(partForm.versionNo) || null,
          defaultUnitPrice: Number(partForm.defaultUnitPrice || 0),
          isOptimized: !!partForm.isOptimized,
          enabled: !!partForm.enabled,
          description: trimText(partForm.description) || null
        }
        if (partForm.id) {
          await MasterDataAPI.updatePart(partForm.id, payload)
          ElMessage.success('配件资料已更新')
        } else {
          await MasterDataAPI.createPart(payload)
          ElMessage.success('配件资料已创建')
        }
        partDialogVisible.value = false
        await fetchParts()
      } catch (err) {
        ElMessage.error(err?.message || '保存配件资料失败')
      } finally {
        savingPart.value = false
      }
    }

    const removeDevice = async (row) => {
      try {
        await ElMessageBox.confirm(`确定删除设备资料“${row.deviceName}”吗？`, '提示', { type: 'warning' })
        await MasterDataAPI.deleteDeviceArchive(row.id)
        ElMessage.success('设备资料已删除')
        await fetchDevices()
      } catch (err) {
        if (err !== 'cancel' && err !== 'close') {
          ElMessage.error(err?.message || '删除设备资料失败')
        }
      }
    }

    const removeChain = async (row) => {
      try {
        await ElMessageBox.confirm(`确定删除合作连锁店“${row.name}”吗？`, '提示', { type: 'warning' })
        await MasterDataAPI.deleteCooperativeChain(row.id)
        ElMessage.success('合作连锁店已删除')
        await fetchChains()
      } catch (err) {
        if (err !== 'cancel' && err !== 'close') {
          ElMessage.error(err?.message || '删除合作连锁店失败')
        }
      }
    }

    const removePart = async (row) => {
      try {
        await ElMessageBox.confirm(`确定删除配件资料“${row.standardName}”吗？`, '提示', { type: 'warning' })
        await MasterDataAPI.deletePart(row.id)
        ElMessage.success('配件资料已删除')
        await fetchParts()
      } catch (err) {
        if (err !== 'cancel' && err !== 'close') {
          ElMessage.error(err?.message || '删除配件资料失败')
        }
      }
    }

    const formatDateTime = (value) => {
      if (!value) return '—'
      return new Date(value).toLocaleString('zh-CN')
    }

    const formatMoney = (value) => {
      const amount = Number(value || 0)
      return Number.isFinite(amount) ? `${amount.toFixed(2)} 元` : '0.00 元'
    }

    watch(() => props.initialTab, (value) => {
      activeTab.value = ['products', 'parts', 'chains'].includes(value) ? value : 'products'
    })

    const syncPage = (pageRef, list) => {
      const totalPages = Math.max(1, Math.ceil(list.length / pageSize))
      if (pageRef.value > totalPages) {
        pageRef.value = totalPages
      } else {
        pageRef.value = 1
      }
    }

    const buildImportMessage = (label, result) => {
      const importedCount = Number(result?.importedCount || 0)
      const failedCount = Number(result?.failedCount || 0)
      const errors = Array.isArray(result?.errors) ? result.errors.filter(Boolean) : []
      const lines = [`${label}导入完成：成功 ${importedCount} 条，失败 ${failedCount} 条`]
      if (errors.length) {
        lines.push('失败明细：')
        lines.push(...errors)
      }
      return lines.join('\n')
    }

    const beforeImportDevice = async (file) => {
      if (!file) return false
      try {
        importingDevices.value = true
        const result = await MasterDataAPI.importDeviceArchives(file)
        ElMessageBox.alert(buildImportMessage('设备资料', result), '导入结果', {
          type: result?.failedCount ? 'warning' : 'success'
        })
        await fetchDevices()
      } catch (err) {
        ElMessage.error(err?.message || '导入设备资料失败')
      } finally {
        importingDevices.value = false
      }
      return false
    }

    const beforeImportPart = async (file) => {
      if (!file) return false
      try {
        importingParts.value = true
        const result = await MasterDataAPI.importParts(file)
        ElMessageBox.alert(buildImportMessage('配件资料', result), '导入结果', {
          type: result?.failedCount ? 'warning' : 'success'
        })
        await fetchParts()
      } catch (err) {
        ElMessage.error(err?.message || '导入配件资料失败')
      } finally {
        importingParts.value = false
      }
      return false
    }

    const downloadDeviceTemplate = async () => {
      try {
        await MasterDataAPI.downloadDeviceTemplate()
        ElMessage.success('设备资料导入模板已开始下载')
      } catch (err) {
        ElMessage.error(err?.message || '下载设备资料模板失败')
      }
    }

    const downloadPartTemplate = async () => {
      try {
        await MasterDataAPI.downloadPartTemplate()
        ElMessage.success('配件资料导入模板已开始下载')
      } catch (err) {
        ElMessage.error(err?.message || '下载配件资料模板失败')
      }
    }

    watch(filteredChains, (list) => syncPage(currentChainPage, list))
    watch(filteredDevices, (list) => syncPage(currentDevicePage, list))
    watch(filteredParts, (list) => syncPage(currentPartPage, list))
    watch(filteredProducts, (list) => syncPage(currentProductPage, list))

    onMounted(async () => {
      await Promise.all([fetchChains(), fetchDevices(), fetchParts(), fetchProducts(), fetchBrands()])
    })

    return {
      userStore,
      FEATURE_DEVICE_ARCHIVES,
      canManageMasterData,
      activeTab,
      chainKeyword,
      deviceKeyword,
      partKeyword,
      productKeyword,
      currentChainPage,
      currentDevicePage,
      currentPartPage,
      currentProductPage,
      pageSize,
      loadingChains,
      loadingDevices,
      loadingParts,
      loadingProducts,
      savingChain,
      savingDevice,
      savingPart,
      savingProduct,
      syncingLlms,
      importingDevices,
      importingParts,
      cooperativeChains,
      filteredChains,
      filteredDevices,
      filteredParts,
      filteredProducts,
      paginatedChains,
      paginatedDevices,
      paginatedParts,
      paginatedProducts,
      brands,
      chainDialogVisible,
      deviceDialogVisible,
      partDialogVisible,
      productDialogVisible,
      chainForm,
      deviceForm,
      partForm,
      productForm,
      imageDirOptions,
      imageDir,
      imageFiles,
      loadingImageFiles,
      pickedImage,
      fetchChains,
      fetchDevices,
      fetchParts,
      fetchProducts,
      openChainDialog,
      openDeviceDialog,
      openPartDialog,
      openProductDialog,
      onImageDirChange,
      onPickedImageChange,
      triggerLlmsSync,
      beforeImportDevice,
      beforeImportPart,
      downloadDeviceTemplate,
      downloadPartTemplate,
      submitChain,
      submitDevice,
      submitPart,
      submitProduct,
      removeChain,
      removeDevice,
      removePart,
      removeProduct,
      getChainName,
      getDeviceStatusText,
      formatDateTime,
      formatMoney
    }
  }
}
</script>

<style scoped>
.master-data-management {
  padding: 8px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
}

.toolbar-input {
  width: 320px;
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  margin-top: 4px;
}

.image-picker {
  display: flex;
  gap: 8px;
  width: 100%;
}
</style>
